import classnames from 'classnames';
import React, { FC, useCallback } from 'react';
import { FormattedMessage } from 'react-intl';
import { BoardsOnboardingStep } from '../../../constants/Feature';
import { HELP_LINKS } from '../../../constants/HelpLinks';
import { useMMGRSettings } from '../../../utils/hoc/withMMGRSettings';
import { useBoardsOnboarding } from '../../../utils/hooks/onboarding/useBoardsOnboarding';
import { Button, FloatingNotification, FullHeightLoader, HelpLink } from '../../common';
import { GenericLayout } from '../Generic';
import { ILayout } from '../types';
import { BoardCard } from './BoardCard';
import styles from './BoardsLayout.scss';
import { CreateNewBoard } from './CreateNewBoard';

export const BoardsLayout: FC<ILayout> = ({
  className,
  isLoading,
  hasMoreItems,
  children,
  onClick,
  onContextMenu,
  loadMoreItems,
  resetFilesStream,
  itemsCount,
  extendedFiltersElement,
  selectItem,
}) => {
  const {
    mmgrSettings: { hasMultipleSites },
  } = useMMGRSettings();

  const { isStepActive: isBoardsOnboardingStepActive, completeStep } = useBoardsOnboarding();

  const handleBoardsOnboardingNotificationClose = useCallback(() => {
    completeStep(BoardsOnboardingStep.FloatingNotification);
  }, [completeStep]);

  const itemClassName = styles.boardsGridElement;

  return (
    <>
      {hasMultipleSites && !isLoading && isBoardsOnboardingStepActive(BoardsOnboardingStep.FloatingNotification) && (
        <FloatingNotification
          className={styles.boardsOnboardingNotification}
          type="dark"
          text={
            <FormattedMessage
              id="boards.floating-notification-onboarding"
              values={{
                learnMoreButton: (
                  <HelpLink
                    className={styles.boardsOnboardingNotificationButton}
                    Component={Button}
                    article={HELP_LINKS.BOARDS}
                    size="tiny"
                  >
                    <FormattedMessage id="common.learn-more" />
                  </HelpLink>
                ),
              }}
            />
          }
          dataHook="boards-floating-notification-onboarding"
          onClose={handleBoardsOnboardingNotificationClose}
        />
      )}

      <GenericLayout
        className={classnames(className, styles.boardLayout)}
        isLoading={isLoading}
        hasMoreItems={hasMoreItems}
        itemsClassName={styles.container}
        onClick={onClick}
        onContextMenu={onContextMenu}
        loadMoreItems={loadMoreItems}
        dataHook="boards-layout"
        resetFilesStream={resetFilesStream}
        itemsCount={itemsCount}
        extendedFiltersElement={extendedFiltersElement}
        selectItem={selectItem}
      >
        <CreateNewBoard className={classnames(itemClassName, styles.responsiveHeight)} />

        {children({
          Board: BoardCard,
          LoadingIndicator: FullHeightLoader,
          itemClassName,
          boardCardExternalClassName: styles.responsiveHeight,
        })}
      </GenericLayout>
    </>
  );
};
